import React, { useState, useEffect, useCallback } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import {
  OrganizationTitle,
  OrganizationTable,
  OrganizationTd,
  OrganizationTh,
  OrganizationBody,
  AddButton,
  OrganizationTdButton
} from '@this/components/organization/organization.style';
import { ButtonType } from '@this/shared/atoms/button';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { BusinessEkinetAccount } from '@this/domain/business_ekinet_account';
import BusinessEkinetAccountForm from '@this/components/organization/business_ekinet_accounts/business_ekinet_accounts_form';
import { MasterListContainer } from '@this/components/organization/master_list_container';
import { WithConfirmModal } from '@this/shared/ui/feedbacks/modal/confirm_modal';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { Fetcher, HTTPError } from '@this/src/util';
import Notification from '../../../notification';

type Props = { availableOptions: string[] } & RouteComponentProps;

function getStyles() {
  return {
    passwordCell: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    } as React.CSSProperties,
    visibilityToggle: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center'
    } as React.CSSProperties
  };
}

interface BusinessEkinetAccountListResponse {
  business_ekinet_accounts: BusinessEkinetAccount[];
  total_page: number;
}

const BusinessEkinetAccounts: React.FC<Props> = () => {
  const styles = getStyles();
  const [businessEkinetAccounts, setBusinessEkinetAccounts] = useState<BusinessEkinetAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [editingTarget, setEditingTarget] = useState<BusinessEkinetAccount | null>();
  const [totalPage, setTotalPage] = useState(1);

  const fetchBusinessEkinetAccountList = useCallback(async (page = 1) => {
    Fetcher.get<BusinessEkinetAccountListResponse>('/organization/business_ekinet_accounts.json', { page })
      .then(res => {
        setBusinessEkinetAccounts(res.business_ekinet_accounts.map(account => new BusinessEkinetAccount(account)));
        setTotalPage(res.total_page);
        setLoading(false);
      })
      .catch(error => {
        const err =
          error instanceof HTTPError && error.response?.data.errors
            ? error.response?.data.errors
            : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        Notification.error(err);
      });
  }, []);

  useEffect(() => {
    fetchBusinessEkinetAccountList();
  }, [fetchBusinessEkinetAccountList]);

  const openCreateModal = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.currentTarget.blur();
    setEditingTarget(new BusinessEkinetAccount());
    setIsOpenModal(true);
  };

  const openEditModal = (e: React.MouseEvent<HTMLButtonElement>, account: BusinessEkinetAccount) => {
    e.currentTarget.blur();
    setEditingTarget(account);
    setIsOpenModal(true);
  };

  const closeModal = () => {
    setEditingTarget(null);
    setIsOpenModal(false);
    fetchBusinessEkinetAccountList();
  };

  const handleSubmit = async (account: BusinessEkinetAccount) => {
    if (account.id === 0) {
      await onSubmitCreate(account);
    } else {
      await onSubmitEdit(account);
    }
  };

  const onSubmitCreate = async (account: BusinessEkinetAccount) => {
    Fetcher.post('/organization/business_ekinet_accounts.json', account?.submitParams() ?? {})
      .then(() => {
        closeModal();
        Notification.success('作成完了しました');
      })
      .catch(error => {
        const err =
          error instanceof HTTPError && error.response?.data.errors
            ? error.response?.data.errors
            : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        Notification.error(err);
      });
  };

  const onSubmitEdit = async (account: BusinessEkinetAccount) => {
    Fetcher.put(`/organization/business_ekinet_accounts/${account.id}.json`, account?.submitParams() ?? {})
      .then(() => {
        closeModal();
        Notification.success('編集完了しました');
      })
      .catch(error => {
        const err =
          error instanceof HTTPError && error.response?.data.errors
            ? error.response?.data.errors
            : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        Notification.error(err);
      });
  };

  const onSubmitDelete = async ({ id }: BusinessEkinetAccount) => {
    Fetcher.delete(`/organization/business_ekinet_accounts/${id}.json`, {})
      .then(() => {
        fetchBusinessEkinetAccountList();
        Notification.success('ビジネスえきねっとアカウントを削除しました');
      })
      .catch(error => {
        const err =
          error instanceof HTTPError && error.response?.data.errors
            ? error.response?.data.errors
            : '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
        Notification.error(err);
      });
  };

  const [passwordVisibility, setPasswordVisibility] = useState<Record<number, boolean>>(() =>
    Object.fromEntries(businessEkinetAccounts.map(account => [account.id, false]))
  );

  const togglePasswordVisibility = (id: number) => {
    setPasswordVisibility(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  return (
    <>
      <OrganizationTitle>ビジネスえきねっとマスタ</OrganizationTitle>
      <OrganizationBody>
        {loading ? (
          <Loading />
        ) : (
          <MasterListContainer
            totalPage={totalPage}
            onPaginate={page => fetchBusinessEkinetAccountList(page)}
            createButton={<AddButton onClick={openCreateModal}>新規登録</AddButton>}
            renderContent={() => (
              <OrganizationTable>
                <thead>
                  <tr>
                    <OrganizationTh width="200px">ビジネスえきねっと会員ID</OrganizationTh>
                    <OrganizationTh width="200px">パスワード</OrganizationTh>
                    <OrganizationTh>ユーザー</OrganizationTh>
                    <OrganizationTh width="100px">編集</OrganizationTh>
                    <OrganizationTh width="100px">削除</OrganizationTh>
                  </tr>
                </thead>
                <tbody>
                  {businessEkinetAccounts.map((data: BusinessEkinetAccount) => (
                    <tr key={data.id} data-e2e-id={data.id}>
                      <OrganizationTd>{data.businessEkinetId}</OrganizationTd>
                      <OrganizationTd>
                        <div style={styles.passwordCell}>
                          <span>{passwordVisibility[data.id] ? data.password : '••••••••'}</span>
                          <div style={styles.visibilityToggle} onClick={() => togglePasswordVisibility(data.id)}>
                            {passwordVisibility[data.id] ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </div>
                        </div>
                      </OrganizationTd>
                      <OrganizationTd>
                        {data.users.map(user => (
                          <div key={user.email}>
                            {user.name} ({user.email})
                          </div>
                        ))}
                      </OrganizationTd>
                      <OrganizationTd>
                        <OrganizationTdButton onClick={e => openEditModal(e, data)}>編集</OrganizationTdButton>
                      </OrganizationTd>
                      <OrganizationTd>
                        <WithConfirmModal>
                          {({ open }) => (
                            <OrganizationTdButton
                              buttonType={ButtonType.DANGER}
                              onClick={async e => {
                                e.currentTarget.blur();
                                const description = [
                                  `「${data.businessEkinetId}」を削除してよろしいですか？`,
                                  ...(data.users.length > 0
                                    ? [
                                        ``,
                                        `⚠️ このアカウントを使用している社員がいます`,
                                        ...data.users.map(user => `・${user.name} (${user.email})`),
                                        ``,
                                        `このアカウントに紐付くユーザーの紐付けは解除されます。`
                                      ]
                                    : []),
                                  ``,
                                  `※この操作は取り消せません`
                                ];
                                const { result } = await open({
                                  description,
                                  color: 'danger',
                                  submitButtonLabel: '削除',
                                  options: { size: 'medium' }
                                });
                                if (result) {
                                  onSubmitDelete(data);
                                }
                              }}
                            >
                              削除
                            </OrganizationTdButton>
                          )}
                        </WithConfirmModal>
                      </OrganizationTd>
                    </tr>
                  ))}
                </tbody>
              </OrganizationTable>
            )}
          />
        )}
      </OrganizationBody>
      <BusinessEkinetAccountForm
        open={isOpenModal}
        businessEkinetAccount={editingTarget}
        onAbort={closeModal}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default BusinessEkinetAccounts;
